import React from "react";
import { MetricTile as MetricTileType } from "./types";

interface SectionBlockProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  iconBg?: string;
  iconColor?: string;
  borderClass?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}

export const SectionBlock: React.FC<SectionBlockProps> = ({
  icon,
  title,
  subtitle,
  iconBg = "bg-primary/10",
  iconColor = "text-primary",
  // borderClass no longer used — the card's border now comes from
  // .vi-glass-card (glass-tokens.css) instead of a per-caller Tailwind
  // class, same border for every section regardless of what a caller
  // passes. Left in SectionBlockProps (not removed) so any caller still
  // passing it doesn't get a type error — this is a styling pass, not
  // an API change.
  children,
}) => (
  <div className="vi-glass-card p-6 mb-[18px]">
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-[38px] h-[38px] rounded-[11px] ${iconBg} ${iconColor} flex items-center justify-center text-xl flex-shrink-0`}
        >
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className="font-dm text-[19px] font-semibold text-neutral m-0">
            {title}
          </h3>
          {subtitle && (
            <p className="text-[12.5px] text-grey m-0 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
    {children}
  </div>
);

// MetricTile and LeadText unchanged below
export const MetricTile: React.FC<{ tile: MetricTileType }> = ({ tile }) => {
  const barColor = tile.bar_color === "gold" ? "bg-warning" : "bg-primary";

  return (
    <div className="vi-beg-metric-tile rounded-[13px] p-3.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30">
      <p className="text-[12.5px] text-neutral/70 m-0 mb-1.5 min-h-[34px] leading-tight font-dm">
        {tile.label}
      </p>
      <div className="font-dm text-2xl font-semibold text-neutral leading-none">
        {tile.value}
      </div>
      <p className="text-[11px] text-grey mt-1.5 m-0 font-dm">{tile.term}</p>
      {tile.note && (
        <p className="text-[11px] text-warning font-medium mt-1 m-0 font-dm">
          {tile.note}
        </p>
      )}
      {tile.bar_pct !== undefined && (
        <div className="h-1.5 rounded-full bg-grey/30 mt-2.5 overflow-hidden">
          <div
            className={`h-full rounded-full ${barColor}`}
            style={{ width: `${tile.bar_pct}%` }}
          />
        </div>
      )}
    </div>
  );
};

export const LeadText: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => (
  <p className={`text-[15px] text-neutral/70 font-dm m-0 ${className}`}>
    {children}
  </p>
);
